import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { fetchGiftCards } from "@/lib/api";
import type { GiftCard } from "@/types/wallet";

/**
 * `state.giftCards` — the tenant's gift-card catalogue.
 *
 * Same provenance as `state.vouchers`: socket in the original, REST here. The
 * wallet reads it for the gift-card rows behind the Gift Cards tile (name,
 * code, reward preview, remaining count, expiry) and for the Apply/Remove
 * control on each row.
 */
interface GiftCardsState {
  data: GiftCard[];
  loading: boolean;
}

const initialState: GiftCardsState = { data: [], loading: false };

export const loadGiftCards = createAsyncThunk(
  "giftCards/load",
  async (tenantId: string) => fetchGiftCards(tenantId),
);

const giftCardsSlice = createSlice({
  name: "giftCards",
  initialState,
  reducers: {
    setGiftCards(state, action: PayloadAction<GiftCard[] | null>) {
      state.data = action.payload ?? [];
    },
    resetGiftCards: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadGiftCards.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadGiftCards.fulfilled, (state, action) => {
        state.data = action.payload ?? [];
        state.loading = false;
      })
      .addCase(loadGiftCards.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setGiftCards, resetGiftCards } = giftCardsSlice.actions;
export default giftCardsSlice.reducer;
